import { rm } from 'node:fs/promises';
import { join } from 'node:path';
import * as lancedb from '@lancedb/lancedb';
import { LanceSchema } from '@lancedb/lancedb/embedding';
import { Utf8 } from 'apache-arrow';
import { HuggingFaceEmbeddingFunction } from './lance-embedding-function.js';
import { createLogger } from '../logging/index.js';
import { createDocumentId, createStoreId } from '../types/brands.js';
import { DocumentMetadataSchema } from '../types/document.js';
import { buildFtsContent } from '../utils/code-tokenizer.js';
import type { StoreId, DocumentId } from '../types/brands.js';
import type { EmbeddingConfig } from '../types/config.js';
import type { DocumentMetadata, Document } from '../types/document.js';
import type { Table, Connection } from '@lancedb/lancedb';

const logger = createLogger('lance');

interface LanceDocument {
  id: string;
  content: string;
  fts_content?: string; // content + identifier-split variants for FTS indexing (v3 schema)
  vector: number[];
  metadata: string; // JSON serialized
  [key: string]: unknown;
}

interface SearchHit {
  id: string;
  content: string;
  metadata: string;
  _distance: number;
}

function isSearchHit(value: unknown): value is SearchHit {
  if (typeof value !== 'object' || value === null) return false;
  return (
    'id' in value &&
    'content' in value &&
    'metadata' in value &&
    '_distance' in value &&
    typeof value.id === 'string' &&
    typeof value.content === 'string' &&
    typeof value.metadata === 'string' &&
    typeof value._distance === 'number'
  );
}

interface DocumentRecord {
  id: string;
  content: string;
  metadata: string;
}

function isDocumentRecord(value: unknown): value is DocumentRecord {
  if (typeof value !== 'object' || value === null) return false;
  return (
    'id' in value &&
    'content' in value &&
    'metadata' in value &&
    typeof value.id === 'string' &&
    typeof value.content === 'string' &&
    typeof value.metadata === 'string'
  );
}

/**
 * Parse serialized metadata JSON and return properly branded DocumentMetadata.
 * Uses Zod for validation and createStoreId to brand the storeId.
 */
function parseDocumentMetadata(jsonStr: string): DocumentMetadata {
  const parsed = DocumentMetadataSchema.parse(JSON.parse(jsonStr));
  return {
    ...parsed,
    storeId: createStoreId(parsed.storeId),
  };
}

export class LanceStore {
  private connection: Connection | null = null;
  private readonly tables: Map<string, Table> = new Map();
  private readonly dataDir: string;
  // eslint-disable-next-line @typescript-eslint/prefer-readonly -- set via setDimensions()
  private _dimensions: number | null = null;
  private embeddingFunction: HuggingFaceEmbeddingFunction | null = null;

  constructor(dataDir: string) {
    this.dataDir = dataDir;
  }

  /**
   * Set the embedding function for auto-embedding queries.
   * Must be called before initialize() for new tables.
   * The embedding function is initialized and its dimensions are used for schema creation.
   */
  async setEmbeddingFunction(config: EmbeddingConfig): Promise<void> {
    this.embeddingFunction = new HuggingFaceEmbeddingFunction({
      model: config.model,
      batchSize: config.batchSize,
      dtype: config.dtype,
      pooling: config.pooling,
      normalize: config.normalize,
      queryPrefix: config.queryPrefix,
      docPrefix: config.docPrefix,
    });
    await this.embeddingFunction.init();
    this._dimensions = this.embeddingFunction.ndims();
  }

  /**
   * Check if embedding function is available for auto-embedding queries.
   */
  hasEmbeddingFunction(): boolean {
    return this.embeddingFunction !== null;
  }

  /**
   * Set the embedding dimensions. Must be called before initialize().
   * This allows dimensions to be derived from the embedding model at runtime.
   * Idempotent: subsequent calls are ignored if dimensions are already set.
   */
  setDimensions(dimensions: number): void {
    this._dimensions ??= dimensions;
  }

  async initialize(storeId: StoreId): Promise<void> {
    if (this._dimensions === null) {
      throw new Error(
        'Dimensions not set. Call setDimensions() or setEmbeddingFunction() before initialize().'
      );
    }

    this.connection ??= await lancedb.connect(this.dataDir);

    const tableName = this.getTableName(storeId);
    const tableNames = await this.connection.tableNames();

    if (!tableNames.includes(tableName)) {
      // Create table with schema that includes embedding function if available
      if (this.embeddingFunction !== null) {
        // Use LanceSchema with embedding function for auto-embedding support
        const schema = LanceSchema({
          id: new Utf8(),
          content: this.embeddingFunction.sourceField(),
          fts_content: new Utf8(), // content + identifier-split variants for FTS
          vector: this.embeddingFunction.vectorField(),
          metadata: new Utf8(),
        });
        const table = await this.connection.createEmptyTable(tableName, schema);
        this.tables.set(tableName, table);
      } else {
        // Create table with initial row (old approach without embedding function)
        const table = await this.connection.createTable(tableName, [
          {
            id: '__init__',
            content: '',
            fts_content: '',
            vector: new Array(this._dimensions).fill(0),
            metadata: '{}',
          },
        ]);
        // Delete the init row
        await table.delete('id = "__init__"');
        this.tables.set(tableName, table);
      }
    } else {
      const table = await this.connection.openTable(tableName);
      this.tables.set(tableName, table);
    }
  }

  async addDocuments(storeId: StoreId, documents: Document[]): Promise<void> {
    const table = await this.getTable(storeId);
    const hasFts = await this.hasFtsContentColumn(storeId);
    if (hasFts) {
      const lanceDocuments: LanceDocument[] = documents.map((doc) => ({
        id: doc.id,
        content: doc.content,
        fts_content: buildFtsContent(doc.content),
        vector: [...doc.vector],
        metadata: JSON.stringify(doc.metadata),
      }));
      await table.add(lanceDocuments);
    } else {
      // Old schema without fts_content — omit field entirely to avoid schema mismatch
      const lanceDocuments = documents.map((doc) => ({
        id: doc.id,
        content: doc.content,
        vector: [...doc.vector],
        metadata: JSON.stringify(doc.metadata),
      }));
      await table.add(lanceDocuments);
    }
  }

  async deleteDocuments(storeId: StoreId, documentIds: DocumentId[]): Promise<void> {
    if (documentIds.length === 0) {
      return;
    }
    const table = await this.getTable(storeId);
    const idList = documentIds.map((id) => `"${id}"`).join(', ');
    await table.delete(`id IN (${idList})`);
  }

  async clearAllDocuments(storeId: StoreId): Promise<void> {
    const table = await this.getTable(storeId);
    await table.delete('id IS NOT NULL');
  }

  async search(
    storeId: StoreId,
    vector: Float32Array,
    limit: number,
    // threshold is kept for API compatibility but filtering is done after normalization
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _threshold?: number
  ): Promise<
    Array<{ id: DocumentId; content: string; score: number; metadata: DocumentMetadata }>
  > {
    const table = await this.getTable(storeId);
    const query = table.vectorSearch(vector).limit(limit).distanceType('cosine');

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const results = (await query.toArray()) as SearchHit[];

    // Return all results - threshold filtering is applied after score normalization
    // in search.service.ts to match displayed scores
    return results.map((r) => {
      const metadata = DocumentMetadataSchema.parse(JSON.parse(r.metadata));
      return {
        id: createDocumentId(r.id),
        content: r.content,
        score: 1 - r._distance,
        // Schema validates structure, cast to branded type
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        metadata: metadata as DocumentMetadata,
      };
    });
  }

  /**
   * Search using a text query with automatic embedding.
   * Requires setEmbeddingFunction() to have been called.
   * Uses the embedding function to compute query embeddings consistently with document embeddings.
   */
  async searchText(
    storeId: StoreId,
    query: string,
    limit: number
  ): Promise<
    Array<{ id: DocumentId; content: string; score: number; metadata: DocumentMetadata }>
  > {
    if (this.embeddingFunction === null) {
      throw new Error(
        'Embedding function not set. Call setEmbeddingFunction() before searchText().'
      );
    }

    // Use the embedding function to compute query embedding
    const queryEmbedding = await this.embeddingFunction.computeQueryEmbeddings(query);
    const table = await this.getTable(storeId);
    const searchQuery = table.vectorSearch(queryEmbedding).limit(limit).distanceType('cosine');

    const rawResults: unknown[] = await searchQuery.toArray();
    const results = rawResults.filter(isSearchHit);

    return results.map((r) => ({
      id: createDocumentId(r.id),
      content: r.content,
      score: 1 - r._distance,
      metadata: parseDocumentMetadata(r.metadata),
    }));
  }

  async createFtsIndex(storeId: StoreId): Promise<void> {
    const table = await this.getTable(storeId);
    // Use fts_content column (with identifier-split variants) if available, fall back to content
    const ftsColumn = (await this.hasFtsContentColumn(storeId)) ? 'fts_content' : 'content';
    await table.createIndex(ftsColumn, {
      config: lancedb.Index.fts({
        stem: true,
        removeStopWords: false,
        lowercase: true,
        language: 'English',
      }),
    });
  }

  /**
   * Check if a table has the fts_content column (v3 schema).
   * Tables created before the FTS improvement only have content.
   */
  private async hasFtsContentColumn(storeId: StoreId): Promise<boolean> {
    try {
      const table = await this.getTable(storeId);
      const schema = await table.schema();
      return schema.fields.some((f) => f.name === 'fts_content');
    } catch {
      return false;
    }
  }

  async fullTextSearch(
    storeId: StoreId,
    query: string,
    limit: number
  ): Promise<
    Array<{ id: DocumentId; content: string; score: number; metadata: DocumentMetadata }>
  > {
    const table = await this.getTable(storeId);

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const results = (await table.search(query, 'fts').limit(limit).toArray()) as Array<{
      id: string;
      content: string;
      metadata: string;
      _score: number;
    }>;

    return results.map((r) => {
      const metadata = DocumentMetadataSchema.parse(JSON.parse(r.metadata));
      return {
        id: createDocumentId(r.id),
        content: r.content,
        score: r._score,
        // Schema validates structure, cast to branded type
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        metadata: metadata as DocumentMetadata,
      };
    });
  }

  /**
   * Get all documents from a store (for training data generation).
   * Returns documents in batches to avoid memory issues with large stores.
   */
  async getAllDocuments(
    storeId: StoreId,
    options?: { limit?: number; offset?: number }
  ): Promise<Array<{ id: DocumentId; content: string; metadata: DocumentMetadata }>> {
    const table = await this.getTable(storeId);
    const limit = options?.limit ?? 10000;
    const offset = options?.offset ?? 0;

    // Query all documents with pagination
    const rawResults: unknown[] = await table.query().limit(limit).offset(offset).toArray();
    const results = rawResults.filter(isDocumentRecord);

    return results.map((r) => ({
      id: createDocumentId(r.id),
      content: r.content,
      metadata: parseDocumentMetadata(r.metadata),
    }));
  }

  /**
   * Count total documents in a store.
   */
  async countDocuments(storeId: StoreId): Promise<number> {
    const table = await this.getTable(storeId);
    return table.countRows();
  }

  async deleteStore(storeId: StoreId): Promise<void> {
    const tableName = this.getTableName(storeId);
    // Connect on-demand - no dimensions needed for listing/dropping tables
    this.connection ??= await lancedb.connect(this.dataDir);
    const tableNames = await this.connection.tableNames();
    if (tableNames.includes(tableName)) {
      await this.connection.dropTable(tableName);
    }
    // Always clear in-memory handle (avoids stale references)
    this.tables.delete(tableName);

    // Remove the .lance directory from disk (even if table didn't exist in LanceDB)
    const lanceDir = join(this.dataDir, `${tableName}.lance`);
    try {
      await rm(lanceDir, { recursive: true, force: true });
    } catch (error) {
      // Non-fatal: log warning but don't fail store deletion
      logger.warn({ lanceDir, error }, 'Failed to remove .lance directory');
    }
  }

  close(): void {
    this.tables.clear();
    if (this.connection !== null) {
      this.connection.close();
      this.connection = null;
    }
  }

  /**
   * Async close for API consistency. Calls sync close() internally.
   * Do NOT call process.exit() after this - let the event loop drain
   * naturally so native threads can complete cleanup.
   */
  closeAsync(): Promise<void> {
    this.close();
    return Promise.resolve();
  }

  private getTableName(storeId: StoreId): string {
    return `documents_${storeId}`;
  }

  private async getTable(storeId: StoreId): Promise<Table> {
    const tableName = this.getTableName(storeId);
    let table = this.tables.get(tableName);
    if (table === undefined) {
      await this.initialize(storeId);
      table = this.tables.get(tableName);
    }
    if (table === undefined) {
      throw new Error(`Table not found for store: ${storeId}`);
    }
    return table;
  }
}
